import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Alert, IconButton, Chip, Tooltip, FormControlLabel, Switch, Portal
} from '@mui/material';
import {
  Visibility, Close, CheckCircle, Warning, Inventory,
  ExpandMore, ChevronRight, Build
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';

// Helper to check if node or descendants have shortages
function hasShortageDescendants(node) {
  if (node.available < node.required) return true;
  if (node.children) {
    return node.children.some(child => hasShortageDescendants(child));
  }
  return false;
}

// Horizontal Tree Node Component
function StockTreeNode({ node, level = 0, hideInStock = false, isRoot = false, isLast = true, parentHasMore = false }) {
  const [expanded, setExpanded] = useState(level < 3);
  const hasShortage = node.available < node.required;
  const shortage = Math.max(0, node.required - node.available);
  
  // Filter children if hiding in-stock
  const visibleChildren = hideInStock 
    ? (node.children || []).filter(child => {
        const childHasShortage = child.available < child.required;
        const childHasShortageDescendants = hasShortageDescendants(child);
        return childHasShortage || childHasShortageDescendants;
      })
    : (node.children || []);
  
  const hasChildren = visibleChildren.length > 0;
  
  // If hiding in-stock and this node is in-stock with no shortage descendants, don't render (unless root)
  if (hideInStock && !hasShortage && !hasChildren && level > 0) {
    return null;
  }

  // Determine colors - root is always grey
  const getBgColor = () => {
    if (isRoot) return 'grey.700';
    return hasShortage ? 'error.dark' : 'success.dark';
  };
  
  const getBorderColor = () => {
    if (isRoot) return 'grey.500';
    return hasShortage ? 'error.light' : 'success.light';
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'flex-start',
      }} 
      onClick={e => e.stopPropagation()}
    >
      {/* Connector line from parent */}
      {level > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          alignSelf: 'stretch',
          position: 'relative'
        }}>
          {/* Horizontal line to node */}
          <Box sx={{ 
            width: 24, 
            height: 2, 
            bgcolor: 'grey.600',
            alignSelf: 'center'
          }} />
        </Box>
      )}
      
      {/* Node content + children container */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        {/* Node box */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) setExpanded(!expanded);
          }}
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: getBgColor(),
            color: 'white',
            border: '2px solid',
            borderColor: getBorderColor(),
            minWidth: 180,
            maxWidth: 240,
            cursor: hasChildren ? 'pointer' : 'default',
            transition: 'all 0.2s',
            '&:hover': hasChildren ? { 
              opacity: 0.9,
              transform: 'scale(1.02)'
            } : {},
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
            {hasChildren && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                width: 18,
                height: 18,
                flexShrink: 0,
                mt: 0.25
              }}>
                {expanded ? 
                  <ExpandMore sx={{ fontSize: 14 }} /> : 
                  <ChevronRight sx={{ fontSize: 14 }} />
                }
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="caption" 
                sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.2, display: 'block' }}
              >
                {node.code}
              </Typography>
              {node.name && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    lineHeight: 1.3, 
                    opacity: 0.85, 
                    display: 'block', 
                    wordBreak: 'break-word',
                    mt: 0.25
                  }}
                  title={node.name}
                >
                  {node.name.length > 35 ? node.name.substring(0, 35) + '...' : node.name}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Work order badge */}
          {node.workOrder && (
            <Chip 
              icon={<Build sx={{ fontSize: 10 }} />}
              label={node.workOrder}
              size="small"
              sx={{ 
                height: 18, 
                fontSize: '0.6rem', 
                mb: 0.5,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
          
          {/* Quantities */}
          {isRoot ? (
            <Box sx={{ 
              fontSize: '0.7rem', 
              opacity: 0.9,
              bgcolor: 'rgba(0,0,0,0.2)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              mt: 0.5
            }}>
              <span>Producing: <strong>{node.required.toFixed(1)}</strong></span>
            </Box>
          ) : (
            <Box sx={{ 
              bgcolor: 'rgba(0,0,0,0.2)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              mt: 0.5
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', opacity: 0.9 }}>
                <span>Need: <strong>{node.required.toFixed(1)}</strong></span>
                <span>Have: <strong>{node.available.toFixed(1)}</strong></span>
              </Box>
              
              {hasShortage && (
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 'bold', 
                  mt: 0.25, 
                  color: '#ffcdd2',
                  textAlign: 'center'
                }}>
                  Short: {shortage.toFixed(1)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        
        {/* Children - horizontal layout */}
        {expanded && hasChildren && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            ml: 0
          }}>
            {/* Horizontal connector line from parent to children branch */}
            <Box sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              width: 24,
              height: 2,
              bgcolor: 'grey.600',
              transform: 'translateY(-50%)',
              display: visibleChildren.length > 0 ? 'block' : 'none'
            }} />
            
            {/* Vertical line connecting children */}
            {visibleChildren.length > 1 && (
              <Box sx={{
                position: 'absolute',
                left: 24,
                top: 20,
                bottom: 20,
                width: 2,
                bgcolor: 'grey.600'
              }} />
            )}
            
            {/* Children nodes */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              pl: 3,
              py: 1
            }}>
              {visibleChildren.map((child, index) => (
                <StockTreeNode 
                  key={`${child.code}-${index}`} 
                  node={child} 
                  level={level + 1}
                  hideInStock={hideInStock}
                  isRoot={false}
                  isLast={index === visibleChildren.length - 1}
                  parentHasMore={index < visibleChildren.length - 1}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Main Dialog
export function StockTreeDialog({ open, onClose, workOrder }) {
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [stats, setStats] = useState({ total: 0, shortages: 0 });
  const [error, setError] = useState(null);
  const [hideInStock, setHideInStock] = useState(false);

  const loadStockTree = useCallback(async () => {
    if (!workOrder) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const woResponse = await manufacturingAPI.workOrders.list({ page_size: 500 });
      const allWorkOrders = woResponse.data.results || woResponse.data || [];
      
      const woByOutputItem = new Map();
      allWorkOrders.forEach(wo => {
        if (wo.output_item) {
          woByOutputItem.set(wo.output_item, wo);
        }
        if (wo.output_item_display) {
          woByOutputItem.set(wo.output_item_display.trim(), wo);
        }
      });

      const explosionCache = new Map();
      
      const getExplosion = async (itemId, quantity) => {
        const key = `${itemId}-${quantity}`;
        if (explosionCache.has(key)) {
          return explosionCache.get(key);
        }
        
        try {
          const response = await manufacturingAPI.bom.explodeRequirements({
            item_id: itemId,
            quantity: quantity
          });
          explosionCache.set(key, response.data);
          return response.data;
        } catch (err) {
          console.warn(`Failed to get explosion for item ${itemId}:`, err);
          return null;
        }
      };

      const buildNode = async (itemId, itemCode, itemName, quantity, visited = new Set()) => {
        const visitKey = `${itemId || itemCode}`;
        if (visited.has(visitKey)) {
          return null;
        }
        visited.add(visitKey);

        const wo = woByOutputItem.get(itemId) || woByOutputItem.get(itemCode);
        const explosion = await getExplosion(itemId, quantity);
        const requirements = explosion?.requirements || [];

        const node = {
          id: itemId,
          code: itemCode || `Item ${itemId}`,
          name: itemName || '',
          required: quantity,
          available: 0,
          workOrder: wo?.work_order_number || null,
          children: []
        };

        for (const req of requirements) {
          const childWo = woByOutputItem.get(req.item_id) || woByOutputItem.get(req.item_code?.trim());
          
          if (childWo && childWo.id !== workOrder.id) {
            const childNode = await buildNode(
              childWo.output_item,
              childWo.output_item_display,
              childWo.output_item_name,
              parseFloat(req.required_quantity) || 0,
              new Set(visited)
            );
            if (childNode) {
              childNode.available = parseFloat(req.available_quantity) || 0;
              childNode.name = childWo.output_item_name || req.item_name || '';
              node.children.push(childNode);
            }
          } else {
            node.children.push({
              id: req.item_id,
              code: req.item_code || `Item ${req.item_id}`,
              name: req.item_name || '',
              required: parseFloat(req.required_quantity) || 0,
              available: parseFloat(req.available_quantity) || 0,
              workOrder: null,
              children: []
            });
          }
        }

        node.children.sort((a, b) => {
          const aShort = a.available < a.required ? 0 : 1;
          const bShort = b.available < b.required ? 0 : 1;
          if (aShort !== bShort) return aShort - bShort;
          return (a.code || '').localeCompare(b.code || '');
        });

        return node;
      };

      const quantityNeeded = workOrder.quantity_to_produce - workOrder.quantity_completed;
      const tree = await buildNode(
        workOrder.output_item,
        workOrder.output_item_display,
        workOrder.output_item_name,
        quantityNeeded
      );

      if (tree) {
        setTreeData(tree);
        const counts = countNodes(tree, true);
        setStats(counts);
      }
      
    } catch (err) {
      console.error('Failed to load stock tree:', err);
      setError('Failed to load stock tree.');
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  useEffect(() => {
    if (open && workOrder) {
      loadStockTree();
    }
  }, [open, workOrder, loadStockTree]);

  const countNodes = (node, isRoot = false) => {
    if (!node) return { total: 0, shortages: 0 };
    
    let total = isRoot ? 0 : 1;
    let shortages = (!isRoot && node.available < node.required) ? 1 : 0;
    
    if (node.children) {
      node.children.forEach(child => {
        const c = countNodes(child, false);
        total += c.total;
        shortages += c.shortages;
      });
    }
    return { total, shortages };
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <Portal>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        PaperProps={{ 
          sx: { 
            maxHeight: '95vh',
            maxWidth: '95vw',
            width: 'auto',
            minWidth: '80vw'
          },
          onClick: stopPropagation,
          onMouseDown: stopPropagation
        }}
        slotProps={{
          backdrop: {
            onClick: (e) => {
              e.stopPropagation();
              onClose();
            }
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5
          }}
          onClick={stopPropagation}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory />
            <Typography variant="h6">Stock Tree: {workOrder?.work_order_number}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ bgcolor: '#1a1a1a', p: 2 }} onClick={stopPropagation}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">Loading material tree...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : treeData ? (
            <Box>
              {/* Controls Section - Horizontal bar */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                mb: 2, 
                p: 2, 
                bgcolor: 'background.paper',
                borderRadius: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }}>
                {/* Left: Legend */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'grey.700', borderRadius: 0.5, border: '2px solid', borderColor: 'grey.500' }} />
                    <Typography variant="body2">Making</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'success.dark', borderRadius: 0.5, border: '2px solid', borderColor: 'success.light' }} />
                    <Typography variant="body2">In Stock</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: 'error.dark', borderRadius: 0.5, border: '2px solid', borderColor: 'error.light' }} />
                    <Typography variant="body2">Shortage</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Build sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">Has WO</Typography>
                  </Box>
                </Box>
                
                {/* Center: Info */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  bgcolor: 'info.dark',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 2
                }}>
                  <Inventory sx={{ fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {workOrder?.output_item_display}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {workOrder?.output_item_name?.substring(0, 40)}{workOrder?.output_item_name?.length > 40 ? '...' : ''} 
                      {' '}• Qty: {workOrder?.quantity_to_produce - workOrder?.quantity_completed}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Right: Toggle + Stats */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: hideInStock ? 'error.dark' : 'grey.800',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: hideInStock ? 'error.main' : 'grey.600'
                  }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={hideInStock} 
                          onChange={(e) => setHideInStock(e.target.checked)}
                          onClick={stopPropagation}
                          color="error"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                          Shortages Only
                        </Typography>
                      }
                      sx={{ m: 0 }}
                      onClick={stopPropagation}
                    />
                  </Box>
                  
                  <Chip 
                    icon={<CheckCircle />} 
                    label={`${stats.total - stats.shortages} OK`} 
                    color="success" 
                    size="small"
                    sx={{ fontWeight: 'bold' }} 
                  />
                  {stats.shortages > 0 && (
                    <Chip 
                      icon={<Warning />} 
                      label={`${stats.shortages} Short`} 
                      color="error" 
                      size="small"
                      sx={{ fontWeight: 'bold' }} 
                    />
                  )}
                </Box>
              </Box>

              {/* Tree - Horizontal scrolling */}
              <Box sx={{ 
                bgcolor: 'rgba(0,0,0,0.3)', 
                borderRadius: 1, 
                p: 3,
                maxHeight: 'calc(95vh - 200px)',
                overflow: 'auto'
              }}>
                <StockTreeNode node={treeData} hideInStock={hideInStock} isRoot={true} />
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: '#1a1a1a' }} onClick={stopPropagation}>
          <Button onClick={onClose} variant="contained">Close</Button>
          <Button onClick={loadStockTree} variant="outlined" disabled={loading}>Refresh</Button>
        </DialogActions>
      </Dialog>
    </Portal>
  );
}

// Button
export const StockTreeButton = React.memo(function StockTreeButton({ workOrder, size = "small" }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const handleOpen = useCallback((e) => { 
    e.stopPropagation();
    e.preventDefault();
    setDialogOpen(true); 
  }, []);
  
  const handleClose = useCallback(() => { 
    setDialogOpen(false); 
  }, []);

  const workOrderRef = React.useRef(workOrder);
  React.useEffect(() => {
    workOrderRef.current = workOrder;
  }, [workOrder]);

  return (
    <>
      <Tooltip title="View Stock Tree" arrow>
        <IconButton 
          size={size} 
          onClick={handleOpen}
          onMouseDown={e => e.stopPropagation()}
          sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
        >
          <Visibility />
        </IconButton>
      </Tooltip>
      {dialogOpen && (
        <StockTreeDialog 
          open={dialogOpen} 
          onClose={handleClose} 
          workOrder={workOrderRef.current} 
        />
      )}
    </>
  );
});

export default StockTreeButton;